import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { AuthService } from '../../guards/auth/auth.service';
import { Router } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { RegisterModel } from '../register/register.model';
import { ResponseModel } from './response.model';
import { PrivacyDialogComponent } from '../../../shared/components/privacy-dialog/privacy-dialog.component';
import { ValidationService } from './validation.service';
import { ProvinceService } from '../../../services/data/province.service';
import { MunicipalityService } from '../../../services/data/municipality.service';
import { CountriesService } from '../../../services/data/countries.service';
import { CountriesModel } from '../../../modules/contact/contact/countries.model';
import { Utils } from '../../../shared/utils';
import { AutofillEvent } from '@angular/cdk/text-field';
import { createScope, createTimeline, Scope } from 'animejs';

@Component({
    selector: 'convfact-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss'],
    standalone: false
})

export class RegisterComponent implements OnInit, AfterViewInit, OnDestroy {

  public form: UntypedFormGroup;
  public showNewPassword = false;
  public checked = false;
  public registerForm: UntypedFormGroup;

  public registerModel: RegisterModel;

  public parametrizationId: number = 1003;

  public provinces: any[] = [];
  public provincesFiltered: any[] = [];
  public municipalities: any[] = [];
  public countries: CountriesModel[] = [];
  public countriesFiltered: CountriesModel[] = [];

  isVisible = false;
  isSubmitting = false;

  private stepsScope?: Scope;

  constructor(
    private authService: AuthService,
    private router: Router,
    private fb: UntypedFormBuilder,
    public snackBar: MatSnackBar,
    public dialog: MatDialog,
    private provinceService: ProvinceService,
    private municipalityService: MunicipalityService,
    private countriesService: CountriesService,
    private elRef: ElementRef<HTMLElement>,
  ) {
    this.registerForm = this.fb.group({
      'Username': new UntypedFormControl('', [Validators.required, ValidationService.emailValidator]),
      'Password': new UntypedFormControl('', [Validators.required, Validators.minLength(8)]),
      'CompanyName': new UntypedFormControl('', Validators.required),
      'Name': new UntypedFormControl('',),
      'VatNumber': new UntypedFormControl('', Validators.required),
      'checked': new UntypedFormControl(false, Validators.required),
      'Newsletter': new UntypedFormControl(false),
      'IsConsultant': new UntypedFormControl(false),
      'countriesCode': new UntypedFormControl(null, [Validators.required, this.autocompleteCountriesValidator(this.countries)]),
      'ProvinceCode': new UntypedFormControl(null, [Validators.required, this.autocompleteProvinceValidator(this.provinces)]),
      'PhoneNumber': new UntypedFormControl('', []),
    });
  }

  ngOnInit(): void {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    this.loadCountries();
    this.loadProvinces();
    this.initializeFilteredItemsAndControl();

    this.registerForm.get('VatNumber').valueChanges.subscribe((value: string) => {

      this.setParametrization(value);

    });
  }

  public setParametrization(vatNumber: string): void {
    const juridical: string[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'N', 'R', 'U', 'V', 'W'];
    const physical: string[] = ['X', 'Y', 'Z', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

    if (Utils.IsNullOrEmpty(vatNumber)) {
      this.parametrizationId = 1003;

      this.registerForm.get('Name').setValidators([]);

      this.registerForm.get('Name').updateValueAndValidity();

      return;
    }

    if (physical.filter(value => value.startsWith(vatNumber.charAt(0).toUpperCase())).length > 0) {

      this.parametrizationId = 3;

      this.registerForm.get('Name').setValidators([Validators.required]);
    }
    else if (juridical.filter(value => value.startsWith(vatNumber.charAt(0).toUpperCase())).length > 0) {

      this.parametrizationId = 2;

      this.registerForm.get('Name').setValidators([]);
    }
    else {

      this.parametrizationId = 1003;

      this.registerForm.get('Name').setValidators([]);
    }

    this.registerForm.get('Name').updateValueAndValidity();

  }

  public openPrivacy() {
    this.dialog.open(PrivacyDialogComponent, {
      width: '680px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
    });
  }

  public login(): void {
    this.router.navigate(['/login']);
  }

  private delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  register(): void {
    if (this.isSubmitting || this.registerForm.invalid) return;

    this.isSubmitting = true;

    const MIN_LOADER_MS = 1500;
    const data = this.getFormData(this.registerForm.value);

    const request$ = this.authService.register(data);

    Promise.all([request$, this.delay(MIN_LOADER_MS)])
      .then(([response]) => {
        let temp = new ResponseModel();
        try {
          this.isVisible = true;
          this.registerForm.reset();
          temp = JSON.parse(response['_body']);
        } catch { }
      })
      .catch((response) => {
        // ERROR
        let temp = new ResponseModel();
        try {
          temp = JSON.parse(response['_body']);
        } catch { /* opcional */ }

        if (temp != null && temp.message != null) {
          this.displayError(temp.message);
        } else {
          this.displayError('No se pudo crear la cuenta.');
        }
      })
      .finally(() => {
        this.isSubmitting = false;
      });
  }

  goLogin() {
    this.router.navigate(['/login']);
  }

  private getFormData(formValue: any): RegisterModel {
    this.registerModel = { ...formValue };
    if (typeof this.registerModel.Newsletter === 'string') {
      this.registerModel.Newsletter = false;
    }

    if (typeof this.registerModel.IsConsultant === 'string') {
      this.registerModel.IsConsultant = false;
    }

    this.registerModel.ParametrizationId = this.parametrizationId;

    if (this.parametrizationId !== 3) {
      this.registerModel.Name = '';
    }

    if (this.registerForm.get('ProvinceCode').status === 'VALID') {

      const findProvince = this.provinces.find(x => x.provinceCode === this.registerForm.get('ProvinceCode').value);

      if (findProvince) {
        this.registerModel.State = findProvince.provinceName;

      }

    }

    if (this.registerForm.get('countriesCode').status === 'VALID') {

      const findCountry = this.countries.find(x => x.numerico === this.registerForm.get('countriesCode').value);

      if (findCountry) {
        this.registerModel.Country = findCountry.countryName;
      }

    }

    return this.registerModel;
  }

  public displayError(error: string): void {
    const options = new MatSnackBarConfig();
    options.duration = 15000;
    this.snackBar.open(error, 'Cerrar', options);
  }

  public showPassword(input: any): void {
    input.type = input.type === 'password' ? 'text' : 'password';
    this.showNewPassword = !this.showNewPassword;
  }

  public loadCountries() {
    this.countriesService.getList({}).subscribe(response => {
      this.countries = response.data;
      this.countriesFiltered = response.data;
      this.updateValidator();
    });
  }

  public loadProvinces() {
    this.provinceService.getList().subscribe(response => {
      this.provinces = response.data;
      this.provincesFiltered = response.data;
    });
  }

  public onChangeProvince() {

    this.registerForm.get('MunicipalityCode').setValue('');

    if (this.registerForm.get('ProvinceCode').value != null && this.registerForm.get('ProvinceCode').value != '')
      this.municipalityService.getList({ province_code: this.registerForm.get('ProvinceCode').value }).subscribe(response => {
        this.municipalities = response.data;
      });
    else
      this.municipalities = [];
  }

  initializeFilteredItemsAndControl() {
    this.provincesFiltered = this.provinces && this.provinces.length > 0 ? [...this.provinces] : [];

    this.countriesFiltered = this.countries && this.countries.length > 0 ? [...this.countries] : [];
  }

  filterItems(value: string) {
    if (this.provinces && this.provinces.length > 0) {
      const filterValue = value.toLowerCase();
      this.provincesFiltered = this.provinces.filter(item =>
        item.provinceName.toLowerCase().includes(filterValue)
      );
    } else {
      this.provincesFiltered = [];
    }
  }

  filterItemsCountries(value: string) {
    if (this.countries && this.countries.length > 0) {
      const filterValue = value.toLowerCase();
      this.countriesFiltered = this.countries.filter(item =>
        item.countryName.toLowerCase().includes(filterValue)
      );
    } else {
      this.countriesFiltered = [];
    }
  }

  displayFn = (item: any) => {
    return this.provinces.filter(c => c.provinceCode == item).length > 0 ? this.provinces.filter(c => c.provinceCode == item)[0].provinceName : '';
  };

  displayFnCountries = (item: any) => {
    return this.countries.filter(c => c.numerico == item).length > 0 ? this.countries.filter(c => c.numerico == item)[0].countryName : '';
  };

  updateValidator(): void {
    this.registerForm.get('countriesCode').setValidators([Validators.required, this.autocompleteCountriesValidator(this.countries)]);
    this.registerForm.get('countriesCode').updateValueAndValidity();
  }

  autocompleteCountriesValidator(options: CountriesModel[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const isValidOption = this.countries.some(op => op.numerico === value);
      return isValidOption ? null : { validCountry: true };
    };
  }

  autocompleteProvinceValidator(options: any[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const isValidOption = this.provinces.some(op => op.provinceCode === value);
      return isValidOption ? null : { validProvince: true };
    };
  }


  onProvinceAutofill(event: AutofillEvent) {
    if (event.isAutofilled) {
      const value = (event.target as HTMLInputElement).value || '';
      const province = this.provinces.find(
        p => p.provinceName.toUpperCase() === value.toUpperCase()
      );

      if (province) {
        this.registerForm.get('ProvinceCode')!.setValue(province.provinceCode);
        this.registerForm.get('ProvinceCode')!.updateValueAndValidity();
      }
    }
  }

  onCountryAutofill(event: AutofillEvent): void {
    if (event.isAutofilled) {
      const value = (event.target as HTMLInputElement).value || '';

      const country = this.countries.find(function (c) {
        return c.countryName.toUpperCase() === value.toUpperCase();
      });

      if (country) {
        this.registerForm.get('countriesCode').setValue(country.numerico);
        this.registerForm.get('countriesCode').updateValueAndValidity();
      }
    }
  }

  public trackByIndex(i: number) { return i; }

  ngAfterViewInit(): void {
    this.initStepsAnimation();
  }

  ngOnDestroy(): void {
    this.stepsScope?.revert();
  }

  private initStepsAnimation(): void {
    try {
      this.stepsScope = createScope({
        root: this.elRef,
        mediaQueries: { reduceMotion: '(prefers-reduced-motion: reduce)' }
      }).add(scope => {
        if (scope.matches['reduceMotion']) { return; }

        const root = this.elRef.nativeElement;
        const items = Array.from(root.querySelectorAll<HTMLElement>('.fct-steps__item'));

        const FILL_DURATION = 2200;
        const CIRCLE_DURATION = 380;

        const timeline = createTimeline({
          loop: true,
          loopDelay: 900,
          defaults: { ease: 'outQuad' }
        });

        timeline.add('.fct-steps__fill', {
          width: ['0%', '100%'],
          duration: FILL_DURATION,
          ease: 'inOutQuad'
        }, 0);

        items.forEach((item, i) => {
          const activateAt = i === 0 ? 0 : Math.round((FILL_DURATION * i) / (items.length - 1)) - CIRCLE_DURATION / 2;
          timeline
            .add(item.querySelector('.fct-steps__circle'), {
              backgroundColor: ['rgba(41,50,63,1)', 'rgba(227,253,100,1)'],
              borderColor: ['rgba(255,255,255,0.14)', 'rgba(227,253,100,1)'],
              scale: [1, 1.16, 1],
              duration: CIRCLE_DURATION,
              ease: 'outBack(1.8)'
            }, Math.max(0, activateAt))
            .add(item.querySelector('.fct-steps__icon'), { opacity: [1, 0], duration: CIRCLE_DURATION * 0.6 }, Math.max(0, activateAt))
            .add(item.querySelector('.fct-steps__check'), { opacity: [0, 1], duration: CIRCLE_DURATION * 0.6 }, Math.max(0, activateAt) + CIRCLE_DURATION * 0.3);
        });
      });
    } catch (e) {
      console.warn('No se pudo iniciar la animación del panel de marca', e);
    }
  }

}
